import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import {
    DownloadableAttachmentState,
    initialDownloadableAttachmentState
} from '@/components/common/downloadable-attachment/downloadable-attachment.model'
import { Nullable } from '@/shared/types/app.types'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { ViewerType } from '@/modules/viewerModule/ViewerModule.models'
import { ATTACHMENT_MIME_TYPES } from '@/api/resources/attachment/attachmentTypeService'
import { AttachmentResponse, ATTACHMENT_ENTITY_TYPE } from '@/api/services/common/attachment/common-attachment.dto'

const patientAttachmentService = () => import('@/api/resources/attachment/patientAttachmentService')
const requestAttachmentService = () => import('@/api/resources/attachment/requestAttachmentService')
const caseAttachmentService = () => import('@/api/resources/attachment/caseAttachmentService')
const sampleAttachmentService = () => import('@/api/resources/attachment/sampleAttachmentService')
const portionAttachmentService = () => import('@/api/resources/attachment/portionAttachmentService')
const subportionAttachmentService = () => import('@/api/resources/attachment/subportionAttachmentService')
const complementaryAttachmentService = () => import('@/api/resources/attachment/complementaryAttachmentService')
const incidentAttachmentService = () => import('@/api/resources/attachment/incidentAttachmentService')

@Component
export default class DownloadableAttachment extends Vue {
    @Prop() entitiesIdArray!: number[]
    @Prop() entityLabel!: string
    @Prop() attachmentEntityType!: ATTACHMENT_ENTITY_TYPE

    @Watch('entitiesIdArray')
    async onEntitiesIdArrayChanged() {
        await this.loadAttachments()
    }

    state: DownloadableAttachmentState
    constructor() {
        super()
        this.state = initialDownloadableAttachmentState()
    }

    canDownloadAttachment(attachment:AttachmentResponse): boolean {
        return attachment.mimetype !== ATTACHMENT_MIME_TYPES.URI
    }

    async mounted() {
        await this.loadAttachments()
    }

    async loadAttachments(): Promise<void> {
        this.state.attachmentService = await this.getAttachmentServiceByAttachmentEntity(this.attachmentEntityType)
        const [error, response] = await this.$_toCall(this.state.attachmentService?.getAttachments(...this.entitiesIdArray), true)
        if (!error) {
            this.state.attachments = response
        }
    }

    async getAttachmentServiceByAttachmentEntity(attachmentEntityType: ATTACHMENT_ENTITY_TYPE): Promise<any> {
        let attachmentService = null
        switch (attachmentEntityType) {
        case ATTACHMENT_ENTITY_TYPE.PATIENT:
            await patientAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        case ATTACHMENT_ENTITY_TYPE.REQUEST:
            await requestAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        case ATTACHMENT_ENTITY_TYPE.CASE:
            await caseAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        case ATTACHMENT_ENTITY_TYPE.SAMPLE:
            await sampleAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        case ATTACHMENT_ENTITY_TYPE.PORTION:
            await portionAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        case ATTACHMENT_ENTITY_TYPE.SUBPORTION:
            await subportionAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        case ATTACHMENT_ENTITY_TYPE.COMPLEMENTARY:
            await complementaryAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        case ATTACHMENT_ENTITY_TYPE.INCIDENT:
            await incidentAttachmentService().then(service => {
                attachmentService = service.default
            })
            break
        }
        return attachmentService
    }

    async openAttachment(attachment: AttachmentResponse) {
        if (attachment.mimetype.startsWith(ATTACHMENT_MIME_TYPES.IMAGE)) {
            this.openViewer(attachment)
        } else if (attachment.mimetype === ATTACHMENT_MIME_TYPES.URI) {
            const supService = (await subportionAttachmentService()).default
            const response = await this.$_toCallUnique(supService.getUrlForOpenViewer(
                this.entitiesIdArray[0], // laboratory id
                this.entitiesIdArray[1], // case id
                this.entitiesIdArray[2], // sample id
                this.entitiesIdArray[3], // portion id
                this.entitiesIdArray[4], // subportion id
                attachment.attachmentId)
            )
            if (response) {
                this.$_openUrl({ url: response })
            }
        } else {
            this.openUrl(attachment.attachmentId)
        }
    }

    openUrl(attachmentId: number): void {
        const attachmentUrl = this.state.attachmentService.getUrlFile(...this.entitiesIdArray, attachmentId)
        if (attachmentUrl) {
            window.open(attachmentUrl, '_blank')
        }
    }

    openViewer(attachment: AttachmentResponse): void {
        viewerService.openViewer({
            router: this.$router,
            viewerType: ViewerType.STATIC_VIEWER,
            laboratoryId: this.entitiesIdArray[0],
            caseId: this.entitiesIdArray[1],
            sampleId: this.entitiesIdArray[2],
            attachmentId: attachment.attachmentId,
            label: this.entityLabel,
            portionId: this.entitiesIdArray.length > 3 ? this.entitiesIdArray[3] : undefined,
            subportionId: this.entitiesIdArray.length > 4 ? this.entitiesIdArray[4] : undefined
        })
    }

    private fileSize(attachment: AttachmentResponse): string {
        if (attachment.length < 1024) {
            return attachment.length + ' Bytes'
        } else if (attachment.length < 1048576) {
            return Math.round(attachment.length / 1024) + ' KB'
        } else {
            return Math.round(attachment.length / 1048576) + ' MB'
        }
    }

    getUrlFile(attachmentId: number): void {
        this.state.attachmentService.getUrlFile(...this.entitiesIdArray, attachmentId)
    }

    getThumbnailImage(attachment: AttachmentResponse): Nullable<string> {
        if (!attachment.thumbnailBytes) {
            return null
        } else if (attachment.thumbnailBytes.startsWith('data:')) {
            return attachment.thumbnailBytes
        } else {
            return `data:${attachment.mimetype};base64, ${attachment.thumbnailBytes}`
        }
    }

    async deleteAttachmentConfirmation(attachment: AttachmentResponse): Promise<void> {
        const deleteConfirmation = await this.$_showConfirmationModal()
        if (deleteConfirmation) {
            await this._deleteAttachment(attachment)
        }
    }
    private async _deleteAttachment(attachment: AttachmentResponse): Promise<void> {
        const [err] = await this.$_toCall(this.state.attachmentService.deleteAttachment(...this.entitiesIdArray, attachment.attachmentId), true)
        if (!err) {
            this.$emit('attachmentDeleted')
            await this.loadAttachments()
        }
    }

    async download(attachment: AttachmentResponse, index: number): Promise<void> {
        if (attachment.attachmentId) {
            const [err, response] = await this.$_toCall(this.state.attachmentService.getFile(...this.entitiesIdArray, attachment.attachmentId), true)
            if (!err) {
                // @ts-ignore
                saveAs(response, this.state.attachments[index].fileName)
            }
        }
    }
}
